import React, { useMemo, useRef, useState } from "react";

import type { EndorsementResponse } from "@tm/insurance/api";
import { submitEndorsement } from "@tm/insurance/api";
import {
  AnchorTag,
  FlexComp,
  FormattedText,
  ImageComponent,
  LandingHeaderMobile,
  ReactPortalComp,
  SpanTag,
  Spinner,
} from "@tm/static";
import { useBreakpointV2 } from "@tm/ui-widgets";
import { theme } from "@tm/utility/global";
import CommonModalDweb from "../../../../components/CommonModalDweb/CommonModalDweb";
import { ORDER_DETAIL_V2_ASSETS } from "../../constants";
import type {
  AddNomineeModalProps,
  EndorsementView,
  Entity,
  FormConfig,
  FormDataItem,
} from "../../types";
import {
  AddButton,
  EditTravellersSheetStyle,
  ModalFooter,
  ModalFooterActions,
  SaveButton,
  SheetContent,
  SheetFooter,
  StickyErrorBanner,
  StickyErrorIcon,
  StickyErrorText,
  SubmitModalWrapper,
  TravellerFormSection,
} from "../EditTravellersModal";
import { FormCard, FormRendererFields, useFormRenderer } from "../FormRenderer";
import {
  convertYYYYMMDDToDDMMYYYY,
  normalizeFormConfig,
} from "../FormRenderer/formUtils";
import { InfoModalContent } from "../InfoModalContent";

// ── Entity data extractor for nominee form ─────────────────────────────

const extractNomineeData = (entity: Entity): Record<string, unknown> => {
  const nomineeDetails = Array.isArray(entity.data?.["nomineeDetails"])
    ? entity.data["nomineeDetails"]
    : [];
  return nomineeDetails.length > 0
    ? (nomineeDetails[0] as Record<string, unknown>)
    : {};
};

// ── Build endorsement payload ──────────────────────────────────────────

const buildEndorsementPayload = (
  entities: Entity[],
  formData: FormDataItem[],
  expandedTravellers: Record<number, boolean>,
  formConfig: FormConfig
) => {
  const updatedEntities: Entity[] = entities.map((entity, index) => {
    const nomineeData = formData[index];
    const isExpanded = expandedTravellers[index];

    if (!isExpanded) {
      return { ...entity, data: { ...entity.data, nomineeDetails: [] } };
    }

    const updatedData: Record<string, any> = { ...entity.data };
    const nominee: Record<string, any> = {};

    formConfig.fields.forEach((field) => {
      let value = String((nomineeData[field.name] as string | undefined) || "");
      if (field.type === "DATE" && value) {
        value = convertYYYYMMDDToDDMMYYYY(value);
      }
      nominee[field.name] = value;
    });

    formConfig.dynamicFields?.forEach((df) => {
      const dynamicData = nomineeData.dynamicFields?.[df.triggerId];
      if (dynamicData?.visible) {
        df.fields.forEach((field) => {
          nominee[field.name] = String(
            (dynamicData[field.name] as string | undefined) || ""
          );
        });
      }
    });

    updatedData["nomineeDetails"] = [nominee];
    return { ...entity, data: updatedData };
  });

  const payload = updatedEntities.map((e) => ({ entityId: e.id, ...e.data }));
  return { payload, updatedEntities };
};

// ── Component ──────────────────────────────────────────────────────────

export const AddNomineeModal: React.FC<AddNomineeModalProps> = ({
  isOpen,
  onClose,
  formConfig: rawFormConfig,
  entities,
  bookingId,
  onSave,
  onEndorsementSuccess,
  modalTitle = "Add Nominees",
  saveButtonLabel = "SAVE DETAILS",
}) => {
  const device = useBreakpointV2();

  const formConfig = useMemo(
    () => normalizeFormConfig(rawFormConfig),
    [rawFormConfig]
  );

  // ── Shared form state ────────────────────────────────────────────────

  const {
    formData,
    setFormData,
    errors,
    setErrors,
    initialDataRef,
    getFieldRef,
    handleMaskedFocus,
    handleMaskedBlur,
    handleFieldChange,
    handleDynamicFieldChange,
    toggleDynamicField,
    validateAllFields,
    hasFormDataChanged: formDataChanged,
  } = useFormRenderer({
    isOpen,
    formConfig,
    entities,
    extractEntityData: extractNomineeData,
  });

  // ── Expansion / endorsement state (nominee-specific) ─────────────────

  const [expandedTravellers, setExpandedTravellers] = useState<
    Record<number, boolean>
  >({});
  const [isLoading, setIsLoading] = useState(false);
  const [endorsementView, setEndorsementView] =
    useState<EndorsementView>("form");
  const [endorsementData, setEndorsementData] =
    useState<EndorsementResponse | null>(null);
  const [apiErrorMessage, setApiErrorMessage] = useState<string | null>(null);
  const pendingRefreshRef = useRef(false);
  const prevIsOpenRef = useRef(false);

  // Initialize expansion state when modal opens
  React.useEffect(() => {
    if (isOpen && entities.length > 0) {
      const isJustOpened = !prevIsOpenRef.current;
      prevIsOpenRef.current = true;

      const expansion: Record<number, boolean> = {};
      entities.forEach((entity, idx) => {
        const nomineeDetails = Array.isArray(entity.data?.["nomineeDetails"])
          ? entity.data["nomineeDetails"]
          : [];
        expansion[idx] = nomineeDetails.length > 0;
      });
      setExpandedTravellers(expansion);

      if (isJustOpened) {
        setEndorsementView("form");
        setEndorsementData(null);
        setApiErrorMessage(null);
        setIsLoading(false);
      }
    } else if (!isOpen) {
      prevIsOpenRef.current = false;
    }
  }, [isOpen, entities]);

  // ── Clear API error on field edit ──────────────────────────────────────

  const wrappedHandleFieldChange = (
    entityIndex: number,
    fieldKey: string,
    value: string
  ) => {
    if (apiErrorMessage) setApiErrorMessage(null);
    handleFieldChange(entityIndex, fieldKey, value);
  };

  const wrappedHandleDynamicFieldChange = (
    entityIndex: number,
    triggerId: string,
    fieldKey: string,
    value: string
  ) => {
    if (apiErrorMessage) setApiErrorMessage(null);
    handleDynamicFieldChange(entityIndex, triggerId, fieldKey, value);
  };

  // ── Nominee-specific helpers ─────────────────────────────────────────

  const toggleTravellerExpansion = (idx: number) => {
    setExpandedTravellers((p) => ({ ...p, [idx]: !p[idx] }));
  };

  const removeNominee = (idx: number) => {
    const updated = [...formData];
    updated[idx] = {};
    formConfig.fields.forEach((f) => {
      updated[idx][f.name] = "";
    });
    updated[idx].dynamicFields = {};
    setFormData(updated);
    setExpandedTravellers((p) => ({ ...p, [idx]: false }));
    setErrors((p) => {
      const u = { ...p };
      delete u[idx];
      return u;
    });
  };

  const hadNomineeInitially = (idx: number): boolean => {
    const initial = initialDataRef.current[idx];
    if (!initial) return false;
    for (const f of formConfig.fields) {
      if (String(initial[f.name] || "").trim()) return true;
    }
    if (formConfig.dynamicFields) {
      for (const df of formConfig.dynamicFields) {
        const d = initial.dynamicFields?.[df.triggerId];
        if (d?.visible) {
          for (const f of df.fields) {
            if (String(d[f.name] || "").trim()) return true;
          }
        }
      }
    }
    return false;
  };

  const hasFormDataChanged = (): boolean => {
    for (let i = 0; i < entities.length; i++) {
      if (!expandedTravellers[i] && hadNomineeInitially(i)) return true;
    }
    return formDataChanged((idx) => !!expandedTravellers[idx]);
  };

  // ── Save / Close ─────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!validateAllFields((idx) => !!expandedTravellers[idx])) return;

    setIsLoading(true);
    try {
      const { payload, updatedEntities } = buildEndorsementPayload(
        entities,
        formData,
        expandedTravellers,
        formConfig
      );
      const res = await submitEndorsement({ bookingId, payload });
      setEndorsementData(res);
      const status = (res?.status ?? "").toUpperCase();

      if (status === "SUCCESS") {
        setEndorsementView("success");
        setApiErrorMessage(null);
        initialDataRef.current = JSON.parse(JSON.stringify(formData));
        await Promise.resolve(onSave(updatedEntities));
        pendingRefreshRef.current = true;
      } else if (status === "ERROR" || status === "VALIDATION_ERROR") {
        setApiErrorMessage(
          res?.desc ||
            res?.subtitle ||
            res?.title ||
            "An error occurred. Please try again."
        );
        setEndorsementView("form");
      } else {
        setEndorsementView("generating");
      }
    } catch {
      setApiErrorMessage("An error occurred. Please try again.");
      setEndorsementView("form");
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (pendingRefreshRef.current) {
      pendingRefreshRef.current = false;
      onEndorsementSuccess?.();
    }
    onClose();
  };

  // ── Render ───────────────────────────────────────────────────────────

  if (!isOpen) return null;

  const isResultView =
    endorsementView === "success" || endorsementView === "generating";

  const formChanged = hasFormDataChanged();
  const removeNomineeLabel =
    formConfig?.commonActions?.removeNominee?.label || "Remove Nominee";

  const renderSaveButton = () => (
    <SaveButton
      onClick={handleSave}
      hasChanges={formChanged}
      isLoading={isLoading}
      disabled={!formChanged || isLoading}
    >
      {isLoading ? (
        <>
          <Spinner
            spinnerWidth={16}
            spinnerColor={theme.whiteText}
            thickness={2}
          />
          <FormattedText value={saveButtonLabel} />
        </>
      ) : (
        <FormattedText value={saveButtonLabel} />
      )}
    </SaveButton>
  );

  const renderFormContent = () => (
    <>
      <TravellerFormSection>
        {entities.map((entity, travellerIndex) => {
          const travellerName =
            entity.data?.["name"] || `Traveller ${travellerIndex + 1}`;
          const isExpanded = expandedTravellers[travellerIndex] ?? false;
          const nomineeData = formData[travellerIndex] || {};

          if (!isExpanded) {
            return (
              <FormCard key={travellerIndex}>
                <FlexComp
                  alignItems="center"
                  justifyContent="space-between"
                  margin="0"
                >
                  <SpanTag
                    fontSize="md"
                    fontWeight="bold"
                    color={theme.greyText}
                  >
                    <FormattedText value={travellerName} />
                  </SpanTag>
                  <AddButton
                    onClick={() => toggleTravellerExpansion(travellerIndex)}
                  >
                    ADD
                  </AddButton>
                </FlexComp>
              </FormCard>
            );
          }

          return (
            <FormCard key={travellerIndex}>
              <FlexComp
                alignItems="center"
                justifyContent="space-between"
                margin="0 0 16px"
              >
                <SpanTag fontSize="md" fontWeight="bold" color={theme.greyText}>
                  <FormattedText value={travellerName} />
                </SpanTag>
                <AnchorTag
                  fontWeight="bold"
                  handleClick={() => removeNominee(travellerIndex)}
                >
                  <FormattedText value={removeNomineeLabel} />
                </AnchorTag>
              </FlexComp>

              <FormRendererFields
                entityIndex={travellerIndex}
                entityData={nomineeData}
                entityErrors={errors[travellerIndex] || {}}
                formConfig={formConfig}
                getFieldRef={getFieldRef}
                handleFieldChange={wrappedHandleFieldChange}
                handleMaskedFocus={handleMaskedFocus}
                handleMaskedBlur={handleMaskedBlur}
                handleDynamicFieldChange={wrappedHandleDynamicFieldChange}
                toggleDynamicField={toggleDynamicField}
                setErrors={setErrors}
              />
            </FormCard>
          );
        })}
      </TravellerFormSection>

      {device === "lg" ? (
        <ModalFooter>
          {apiErrorMessage && (
            <StickyErrorBanner>
              <StickyErrorIcon>
                <ImageComponent
                  src={ORDER_DETAIL_V2_ASSETS.VALIDATION_ERROR_ICON}
                  width="24"
                  height="24"
                  alt="Error"
                />
              </StickyErrorIcon>
              <StickyErrorText>
                <FormattedText value={{ html: apiErrorMessage }} />
              </StickyErrorText>
            </StickyErrorBanner>
          )}
          <ModalFooterActions>{renderSaveButton()}</ModalFooterActions>
        </ModalFooter>
      ) : (
        <>
          {apiErrorMessage && (
            <StickyErrorBanner>
              <StickyErrorIcon>
                <ImageComponent
                  src={ORDER_DETAIL_V2_ASSETS.VALIDATION_ERROR_ICON}
                  width="24"
                  height="24"
                  alt="Error"
                />
              </StickyErrorIcon>
              <StickyErrorText>
                <FormattedText value={{ html: apiErrorMessage }} />
              </StickyErrorText>
            </StickyErrorBanner>
          )}
          <SheetFooter>{renderSaveButton()}</SheetFooter>
        </>
      )}
    </>
  );

  const renderResultView = () => (
    <SubmitModalWrapper>
      <InfoModalContent
        viewType={endorsementView}
        data={endorsementData}
        renderAction={(data) => {
          if (
            endorsementView === "success" &&
            data.cta?.isEnabled !== false &&
            data.cta?.label &&
            data.newPolicyUrl
          ) {
            return (
              <SaveButton
                as="a"
                href={data.newPolicyUrl}
                target="_blank"
                rel="noopener noreferrer"
                hasChanges={true}
                isLoading={false}
                style={{ marginTop: "16px", textDecoration: "none" }}
              >
                <FormattedText value={data.cta.label} />
              </SaveButton>
            );
          }
          return null;
        }}
      />
    </SubmitModalWrapper>
  );

  // Mobile: full-page sheet
  if (device === "sm") {
    return (
      <ReactPortalComp>
        <EditTravellersSheetStyle>
          <LandingHeaderMobile
            displayWhiteHeader
            headerTitle={isResultView ? "" : modalTitle}
            handleBackBtn={handleClose}
          />
          <SheetContent>
            {isResultView ? renderResultView() : renderFormContent()}
          </SheetContent>
        </EditTravellersSheetStyle>
      </ReactPortalComp>
    );
  }

  // Desktop: modal
  return (
    <CommonModalDweb
      customTitle={isResultView ? "" : modalTitle}
      handleClick={handleClose}
      customWidth={isResultView ? "420px" : "720px"}
      customHeight={isResultView ? "auto" : "640px"}
    >
      {isResultView ? renderResultView() : renderFormContent()}
    </CommonModalDweb>
  );
};
